import React, { useState } from "react";
import {
  ThinDropdownWithSearch,
  ThinTextField,
} from "../common/GeneralComponent";
import { fetchData } from "../../utils/functions";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";

const HouseListing = ({ houseHoldListings = [], DqaApproved, DqaRejected }) => {
  const { id } = useParams();

  const isDqaApproved = DqaApproved || false;
  const isRejected =  DqaRejected || false;

  const initialRows =
    houseHoldListings.length > 0
      ? houseHoldListings
      : [
          {
            household_name: "",
            relationship: "",
            gender: "",
            age: "",
            marital_status: "",
            special_needs: "No disability",
          },
        ];

  const [rows, setRows] = useState(initialRows);
  const [errors, setErrors] = useState([]); // [{ household_name: '...', ... }, ...]

  const Gender = [
    { label: "Male", value: "Male" },
    { label: "Female", value: "Female" },
  ];

  // Updated MaritalStatus options to match the model
  const MaritalStatus = [
    { label: "Married", value: "Married" },
    { label: "Widow/ Widower", value: "Widow/ Widower" },
    { label: "Divorced", value: "Divorced" },
    { label: "Separated", value: "Separated" },
    { label: "Never Married", value: "Never Married" },
  ];

  // New Relationship options matching the model
  const Relationship = [
    { label: "Household Head", value: "Household Head" },
    { label: "Spouse(s)", value: "Spouse(s)" },
    { label: "Son", value: "Son" },
    { label: "Daughter", value: "Daughter" },
    { label: "Grand Child", value: "Grand Child" },
    { label: "Father", value: "Father" },
    { label: "Mother", value: "Mother" },
    { label: "Brother", value: "Brother" },
    { label: "Sister", value: "Sister" },
    { label: "Niece", value: "Niece" },
    { label: "Nephew", value: "Nephew" },
    { label: "Son in law", value: "Son in law" },
    { label: "Daughter in law", value: "Daughter in law" },
    { label: "Brother in law", value: "Brother in law" },
    { label: "Sister in law", value: "Sister in law" },
    { label: "Father in law", value: "Father in law" },
    { label: "Mother in law", value: "Mother in law" },
    { label: "Grand Father", value: "Grand Father" },
    { label: "Grand Mother", value: "Grand Mother" },
    { label: "Uncle", value: "Uncle" },
    { label: "Aunt", value: "Aunt" },
    { label: "Step child", value: "Step child" },
    { label: "Adopted son", value: "Adopted son" },
    { label: "Adopted Daughter", value: "Adopted Daughter" },
    {
      label: "Servants / their relatives",
      value: "Servants / their relatives",
    },
    { label: "Other relative", value: "Other relative" },
    { label: "Non-relative", value: "Non-relative" },
    { label: "Step mother", value: "Step mother" },
    { label: "Step father", value: "Step father" },
  ];

  const Disabilities = [
    { label: "Mental disability", value: "Mental disability" },
    { label: "Physical disability", value: "Physical disability" },
    {
      label: "Speech & hearing disability",
      value: "Speech & hearing disability",
    },
    { label: "Visual disability", value: "Visual disability" },
    { label: "No disability", value: "No disability" },
  ];

  const handleInputChange = (index, field, value) => {
    const updatedRows = [...rows];

    if (!updatedRows[index]) updatedRows[index] = {};

    // Clean age input: digits only, max 3 chars, and cap at 95 if above
    if (field === "age") {
      value = String(value ?? "")
        .replace(/\D/g, "")
        .slice(0, 3);

      // If age is 95 or above, set it to 95
      const ageNum = parseInt(value);
      if (!isNaN(ageNum) && ageNum >= 120) {
        value = "120";
      }
    }

    updatedRows[index][field] = value;
    setRows(updatedRows);

    // Clear the error for this field as user edits
    setErrors((prev) => {
      const next = [...(prev || [])];
      if (!next[index]) next[index] = {};
      if (next[index][field]) {
        next[index] = { ...next[index], [field]: undefined };
      }
      return next;
    });
  };

  const validateRows = (data) => {
    const nextErrors = data.map((row) => {
      const e = {};
      const name = (row.household_name || "").trim();
      const rel = (row.relationship || "").trim();
      const gender = row.gender || "";
      const ageStr = String(row.age || "").trim();
      const ms = row.marital_status || "";
      const sn = row.special_needs || "";

      if (!name) e.household_name = "Household name is Required.";
      if (!rel) e.relationship = "Relationship is Required.";
      if (!gender) e.gender = "Gender is Required.";

      // Updated age validation to handle 0 correctly
      if (ageStr === "" || ageStr === null || ageStr === undefined) {
        e.age = "Age is Required.";
      } else {
        const ageNum = Number(ageStr);
        if (!/^\d+$/.test(ageStr)) e.age = "Digits only.";
        else if (ageNum < 0 || ageNum > 120)
          e.age =
            "Enter age between 0 and 120. If age is 120 or above, record '99'.";
      }

      if (Number(ageStr) >= 10) {
        if (!ms) e.marital_status = "Marital status is Required.";
      }
      if (!sn) e.special_needs = "Special needs is Required.";

      return e;
    });

    // Any error present?
    const hasAnyError = nextErrors.some((e) => Object.values(e).some(Boolean));
    return { nextErrors, hasAnyError };
  };

  const handleUpdate = async () => {
    if (!id) {
      toast.error("Household ID is missing from URL.");
      return;
    }

    // Validate before submit
    const { nextErrors, hasAnyError } = validateRows(rows);
    setErrors(nextErrors);
    if (hasAnyError) {
      return;
    }

    const payload = rows.map((row) => {
      const maritalStatus =
        row.marital_status && row.marital_status.trim() !== ""
          ? row.marital_status
          : null;

      return {
        household_listing_id: row.household_listing_id || null,
        household_information_id: id,
        household_name: row.household_name || "",
        relationship: row.relationship || "",
        gender: row.gender || "",
        age: row.age || "",
        marital_status: maritalStatus,
        special_needs: !row.special_needs || row.special_needs === "None" ? "No disability" : row.special_needs,
      };
    });

    try {
      const response = await fetchData(
        `householdlisting-update/${id}`,
        "PUT",
        payload
      );
      if (response?.success) {
        toast.success(response.message || "Update successful");
      } else {
        toast.error(response.message || "Update failed");
      }
    } catch (err) {
      toast.error("Error while updating");
      console.error("Update Error:", err);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        {isDqaApproved && (
          <>
            <h2 className="text-teal-700 text-xl font-semibold">
              Household Listing
            </h2>
            <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
              Approved from DQA
            </span>
          </>
        )}
        {isRejected && (
          <>
            <h2 className="text-teal-700 text-xl font-semibold">
              Household Listing
            </h2>

            <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-semibold">
              Rejected from DQA
            </span>
          </>
        )}
      </div>

      <div className="space-y-6">
        {rows.map((row, index) => (
          <div
            key={index}
            className="border-b border-gray-300 pb-4 grid grid-cols-1 md:grid-cols-3 gap-4"
          >
            {/* Name */}
            <div>
              <ThinTextField
                label="Household member Name"
                type="text"
                placeholder="Name"
                value={row.household_name || ""}
                onChange={(e) =>
                  handleInputChange(index, "household_name", e.target.value)
                }
                disabled={isDqaApproved || isRejected}
              />
              {errors?.[index]?.household_name && (
                <p className="text-sm text-red-600 mt-1">
                  {errors[index].household_name}
                </p>
              )}
            </div>

            {/* Gender */}
            <div>
              <ThinDropdownWithSearch
                label="Gender"
                options={Gender}
                value={row.gender || ""}
                onChange={(value) => handleInputChange(index, "gender", value)}
                required
                disabled={isDqaApproved || isRejected}
              />
              {errors?.[index]?.gender && (
                <p className="text-sm text-red-600 mt-1">
                  {errors[index].gender}
                </p>
              )}
            </div>

            {/* Relationship */}
            <div>
              <ThinDropdownWithSearch
                label="Relationship to head of Household"
                options={Relationship}
                value={row.relationship || ""}
                onChange={(value) =>
                  handleInputChange(index, "relationship", value)
                }
                required
                disabled={isDqaApproved || isRejected}
                placeholder="Select relationship"
              />
              {errors?.[index]?.relationship && (
                <p className="text-sm text-red-600 mt-1">
                  {errors[index].relationship}
                </p>
              )}
            </div>

            {/* Age */}
            <div>
              <ThinTextField
                label="Age in year"
                type="number"
                placeholder="Age"
                value={row.age || ""}
                onChange={(e) =>
                  handleInputChange(index, "age", e.target.value)
                }
                disabled={isDqaApproved || isRejected}
              />
              {errors?.[index]?.age && (
                <p className="text-sm text-red-600 mt-1">{errors[index].age}</p>
              )}
            </div>

            {/* Marital Status (only if age > 10) */}
            {Number(row.age) >= 10 && (
              <div>
                <ThinDropdownWithSearch
                  label="Marital Status"
                  options={MaritalStatus}
                  value={row.marital_status || ""}
                  onChange={(value) =>
                    handleInputChange(index, "marital_status", value)
                  }
                  required
                  disabled={isDqaApproved || isRejected}
                />
                {errors?.[index]?.marital_status && (
                  <p className="text-sm text-red-600 mt-1">
                    {errors[index].marital_status}
                  </p>
                )}
              </div>
            )}

            {/* Special needs */}
            <div>
              <ThinDropdownWithSearch
                label="Special Needs"
                options={Disabilities}
                value={row.special_needs === "None" ? "No disability" : row.special_needs || ""}
                onChange={(value) =>
                  handleInputChange(index, "special_needs", value)
                }
                required
                disabled={isDqaApproved || isRejected}
              />
              {errors?.[index]?.special_needs && (
                <p className="text-sm text-red-600 mt-1">
                  {errors[index].special_needs}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>

      {!isDqaApproved && !isRejected && (
        <div className="flex justify-end mt-6">
          <button
            onClick={handleUpdate}
            className="bg-teal-600 text-white rounded-lg px-4 py-2 hover:bg-teal-700 transition-colors"
          >
            Update
          </button>
        </div>
      )}
    </div>
  );
};

export default HouseListing;
